import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { NotificationRow } from '@/components/notifications/NotificationRow';
import { useAuth } from '@/hooks/useAuth';
import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsQuery,
  useUnreadNotificationCountQuery,
} from '@/hooks/useNotifications';
import {
  resolveInAppNavigationPath,
  resolveNotificationCenterRoute,
} from '@/utils/notificationNavigation';
import { cn } from '@/lib/utils';

const MAXIMUM_UNREAD_BADGE_VALUE = 9;
const NOTIFICATION_BELL_PANEL_TITLE_ID = 'notification-bell-panel-title';

export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { activeScopeCode } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const notificationsQuery = useNotificationsQuery();
  const unreadCountQuery = useUnreadNotificationCountQuery();
  const markReadMutation = useMarkNotificationReadMutation();
  const markAllReadMutation = useMarkAllNotificationsReadMutation();

  const page = notificationsQuery.data;
  const notifications = page === undefined ? [] : page.items;
  const newNotifications = notifications.filter(function isUnread(notification) {
    return notification.isRead === false;
  });
  const earlierNotifications = notifications.filter(function isRead(notification) {
    return notification.isRead === true;
  });
  const unreadCount = unreadCountQuery.data === undefined ? 0 : unreadCountQuery.data;
  const badgeLabel =
    unreadCount > MAXIMUM_UNREAD_BADGE_VALUE
      ? String(MAXIMUM_UNREAD_BADGE_VALUE) + '+'
      : String(unreadCount);

  function closePanel() {
    setIsOpen(false);
  }

  function openNotification(notificationId: string, actionUrl: string | null, isRead: boolean) {
    if (isRead === false) {
      markReadMutation.mutate(notificationId);
    }
    const navigationPath = resolveInAppNavigationPath(actionUrl);
    if (navigationPath !== null) {
      closePanel();
      navigate(navigationPath);
    }
  }

  useEffect(
    function closeOnOutsidePointerDown() {
      if (isOpen === false) {
        return;
      }
      function handlePointerDown(pointerEvent: MouseEvent) {
        const container = containerRef.current;
        if (
          container !== null &&
          pointerEvent.target instanceof Node &&
          container.contains(pointerEvent.target) === false
        ) {
          closePanel();
        }
      }
      document.addEventListener('mousedown', handlePointerDown);
      return function unsubscribe() {
        document.removeEventListener('mousedown', handlePointerDown);
      };
    },
    [isOpen]
  );

  useEffect(
    function closeOnEscape() {
      if (isOpen === false) {
        return;
      }
      function handleKeyDown(keyboardEvent: KeyboardEvent) {
        if (keyboardEvent.key === 'Escape') {
          closePanel();
          const bellButton = bellButtonRef.current;
          if (bellButton !== null) {
            bellButton.focus();
          }
        }
      }
      document.addEventListener('keydown', handleKeyDown);
      return function unsubscribe() {
        document.removeEventListener('keydown', handleKeyDown);
      };
    },
    [isOpen]
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={bellButtonRef}
        type="button"
        aria-label={t('bell.open')}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={function toggleOpen() {
          setIsOpen(function invert(previous) {
            return previous === false;
          });
        }}
        className="relative w-10 h-10 flex items-center justify-center rounded-full bg-zoowhite border border-zooborder text-zooindigo shadow-sm hover:bg-zoovpalelavender transition-colors"
      >
        <Icon name="Bell" />
        {unreadCount > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[10px] font-bold">
            {badgeLabel}
          </span>
        ) : null}
      </button>

      {isOpen === true ? (
        <div
          role="dialog"
          aria-labelledby={NOTIFICATION_BELL_PANEL_TITLE_ID}
          className={cn(
            'fixed inset-x-3 top-16 z-40 flex flex-col max-h-[70vh] bg-zoowhite border border-zooborder rounded-2xl shadow-lg overflow-hidden',
            'sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-96 sm:max-h-[32rem]'
          )}
        >
          <div className="px-4 py-3 border-b border-zooborder flex items-center justify-between gap-2 shrink-0">
            <h3 id={NOTIFICATION_BELL_PANEL_TITLE_ID} className="text-sm font-bold text-zooindigo">
              {t('bell.title')}
            </h3>
            {unreadCount > 0 ? (
              <Button
                type="button"
                variant="link"
                onClick={function markAll() {
                  markAllReadMutation.mutate();
                }}
                isLoading={markAllReadMutation.isPending}
                className="text-xs font-medium"
              >
                {t('bell.markAllRead')}
              </Button>
            ) : null}
          </div>

          {notifications.length === 0 ? (
            <div className="px-4 py-8 flex flex-col items-center text-center">
              <div className="w-11 h-11 rounded-full bg-zoovpalelavender flex items-center justify-center text-zooviolet">
                <Icon name="Bell" />
              </div>
              <p className="mt-3 text-sm text-zoomuted">{t('bell.empty')}</p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto">
              {newNotifications.length > 0 ? (
                <div>
                  <div className="px-4 py-2 bg-zoooffwhite text-[11px] font-bold uppercase tracking-wider text-zoomuted">
                    {t('bell.sections.new')}
                  </div>
                  <ul className="divide-y divide-zooborder">
                    {newNotifications.map(function renderNewNotification(notification) {
                      return (
                        <NotificationRow
                          key={notification.id}
                          notification={notification}
                          onOpen={function open() {
                            openNotification(
                              notification.id,
                              notification.actionUrl,
                              notification.isRead
                            );
                          }}
                          onMarkAsRead={function markRead() {
                            markReadMutation.mutate(notification.id);
                          }}
                          isMarkingAsRead={
                            markReadMutation.isPending === true &&
                            markReadMutation.variables === notification.id
                          }
                        />
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              {earlierNotifications.length > 0 ? (
                <div>
                  <div className="px-4 py-2 bg-zoooffwhite text-[11px] font-bold uppercase tracking-wider text-zoomuted">
                    {t('bell.sections.earlier')}
                  </div>
                  <ul className="divide-y divide-zooborder">
                    {earlierNotifications.map(function renderEarlierNotification(notification) {
                      return (
                        <NotificationRow
                          key={notification.id}
                          notification={notification}
                          onOpen={function open() {
                            openNotification(
                              notification.id,
                              notification.actionUrl,
                              notification.isRead
                            );
                          }}
                          onMarkAsRead={function markRead() {
                            markReadMutation.mutate(notification.id);
                          }}
                          isMarkingAsRead={
                            markReadMutation.isPending === true &&
                            markReadMutation.variables === notification.id
                          }
                        />
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
          <div className="px-4 py-2.5 border-t border-zooborder shrink-0">
            <button
              type="button"
              onClick={function goToNotificationCenter() {
                closePanel();
                navigate(resolveNotificationCenterRoute(activeScopeCode));
              }}
              className="w-full text-center text-xs font-semibold text-zooprimary hover:underline"
            >
              {t('bell.viewAll')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
